import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Ledger from './pages/Ledger';
import Statement from './pages/Statement';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import AcceptInvitation from './pages/AcceptInvitation';
import NewLedger from './pages/NewLedger';
import WeeklyLedgerForm from './components/WeeklyLedgerForm';
import { WeeklyLedger, LedgerEntry, User as UserType, EntryType } from './lib/supabase';

type LedgerWithDetails = WeeklyLedger & {
  user: UserType;
  entries: (LedgerEntry & { entry_type: EntryType })[];
  total: number;
};

function AppContent() {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [isInvitation, setIsInvitation] = useState(false);
  const [editingLedger, setEditingLedger] = useState<LedgerWithDetails | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invitationToken = urlParams.get('invitation');
    setIsInvitation(!!invitationToken);
  }, []);

  if (isInvitation) {
    return <AcceptInvitation />;
  }

  const handleEditLedger = (ledger: LedgerWithDetails) => {
    setEditingLedger(ledger);
    setCurrentView('edit-ledger');
  };

  const handleNewLedgerFromLedger = () => {
    setEditingLedger(null);
    setCurrentView('new-ledger-from-ledger');
  };

  const handleBackToLedger = () => {
    setEditingLedger(null);
    setCurrentView('ledger');
  };

  return (
    <ProtectedRoute requireAdmin={currentView === 'admin'}>
      <Layout currentView={currentView} onViewChange={setCurrentView}>
        {currentView === 'dashboard' && <Dashboard onNewLedger={() => setCurrentView('new-ledger')} />}
        {currentView === 'new-ledger' && (
          <NewLedger
            onBack={() => setCurrentView('dashboard')}
            onSave={() => setCurrentView('ledger')}
          />
        )}
        {currentView === 'new-ledger-from-ledger' && (
          <WeeklyLedgerForm
            onClose={handleBackToLedger}
            onSave={handleBackToLedger}
          />
        )}
        {currentView === 'edit-ledger' && editingLedger && (
          <WeeklyLedgerForm
            ledger={editingLedger}
            onClose={handleBackToLedger}
            onSave={handleBackToLedger}
          />
        )}
        {currentView === 'ledger' && (
          <Ledger
            onNewLedger={handleNewLedgerFromLedger}
            onEditLedger={handleEditLedger}
          />
        )}
        {currentView === 'statement' && <Statement />}
        {currentView === 'reports' && <Reports />}
        {currentView === 'admin' && user?.role === 'admin' && <Admin />}
      </Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
